/**
 * Seeded xorshift32 RNG.
 *
 * Determinism contract: never use `Math.random` inside any `step()` path.
 * RNG state is a single 32-bit integer stored in `GameState.rng`. Each call
 * advances the state; the caller is responsible for writing the new state
 * back. No allocation, no closures.
 *
 * Convention: a seed of 0 is degenerate for xorshift (locks at 0). `rngSeed`
 * normalizes to a nonzero starting state and warms up by 8 iterations so the
 * first emitted value is well-distributed.
 */

/** Advance state by one xorshift32 step. Returns the new state as i32. */
export function rngNext(state: number): number {
  let s = state | 0;
  s ^= s << 13;
  s ^= s >>> 17;
  s ^= s << 5;
  return s | 0;
}

/** Reinterpret an i32 RNG state as a u32 value in [0, 2^32). */
export function rngU32(state: number): number {
  return state >>> 0;
}

/** Uniform integer in [0, n). Caller passes the *advanced* state. n must be >= 1 and <= 2^31. */
export function rngIntBelow(state: number, n: number): number {
  // For n's used by VPGC sims (cell counts, small wave-size jitter), modulo
  // bias is negligible. If a game ever needs unbiased ranges, add a rejection
  // variant here.
  return ((state >>> 0) % (n | 0)) | 0;
}

/** Float in [0, 1) for cosmetic / UI-only use. NEVER use inside step(). */
export function rngFloat01(state: number): number {
  return (state >>> 0) / 0x100000000;
}

/** Normalize a seed into a usable initial RNG state. */
export function rngSeed(seed: number): number {
  let s = seed | 0;
  if (s === 0) s = 0x9e3779b9 | 0; // golden-ratio constant; any nonzero u32 works
  for (let i = 0; i < 8; i++) s = rngNext(s);
  return s;
}
